import { Modal } from "./Modal";
import { useCheatSheetOpen } from "../hooks/useGlobalShortcuts";
import { modKeyLabel } from "../lib/platformKeys";

// To wire: render `<KeyboardCheatSheet />` once inside the Router tree
// in App.tsx. It self-subscribes to the cheat-sheet open flag exposed
// from useGlobalShortcuts; pressing `?` anywhere outside a text input
// opens it. Esc closes (Modal handles that internally).

type Binding = { keys: string[]; label: string };
type Group = { title: string; rows: Binding[] };

// The review rows name the modifier the founder's keyboard carries (0051 S1a):
// `mod` is modKeyLabel(), read at render - the Command glyph on Apple, "Ctrl"
// elsewhere. The review handlers accept either.
const groups = (mod: string): Group[] => [
  {
    title: "Global",
    rows: [
      { keys: ["g", "d"], label: "Go to dashboard" },
      { keys: ["g", "g"], label: "Open graph (current product)" },
      { keys: ["?"], label: "Show this cheat sheet" },
      { keys: ["esc"], label: "Close panel / back to spec" },
    ],
  },
  {
    title: "Focused review",
    rows: [
      { keys: ["←", "→"], label: "Previous / next item" },
      { keys: [mod, "←"], label: "Previous (works inside textarea)" },
      { keys: [mod, "→"], label: "Next (works inside textarea)" },
      { keys: [mod, "↵"], label: "Save & next (questions) / reject with your reason (decisions)" },
      { keys: ["Y"], label: "Confirm decision" },
      { keys: ["N"], label: "Reject decision" },
    ],
  },
  {
    title: "Graph",
    rows: [
      { keys: ["esc"], label: "Close node detail" },
    ],
  },
];

// Modifier keys glue with "+" to the next key; everything else (sequences
// like g d, single-key bindings) glues with "then".
function isModifier(k: string): boolean {
  return k === "⌘" || k === "Ctrl" || k === "⇧" || k === "⌃" || k === "⌥";
}

export function KeyboardCheatSheet() {
  const [open, setOpen] = useCheatSheetOpen();
  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      title="Keyboard shortcuts"
      width="max-w-xl"
    >
      <div className="space-y-6">
        {groups(modKeyLabel()).map((group) => (
          <section key={group.title}>
            <h4 className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-2 pb-1.5 border-b border-ink-800">
              {group.title}
            </h4>
            <ul className="space-y-1.5">
              {group.rows.map((row) => (
                <li
                  key={row.keys.join("+") + row.label}
                  className="flex items-center justify-between gap-4 py-1"
                >
                  <span className="text-[13px] text-ink-200">{row.label}</span>
                  <span className="flex items-center gap-1 shrink-0">
                    {row.keys.map((k, i) => (
                      <span key={`${k}-${i}`} className="flex items-center gap-1">
                        {i > 0 && (
                          <span className="font-mono text-[10px] text-ink-600">
                            {isModifier(row.keys[i - 1]) ? "+" : "then"}
                          </span>
                        )}
                        <kbd className="font-mono text-[11px] text-ink-200 border border-ink-700 bg-ink-950 rounded px-1.5 py-0.5 min-w-[1.5rem] text-center">
                          {k}
                        </kbd>
                      </span>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}

        <p className="font-mono text-[10px] text-ink-500 pt-2 border-t border-ink-800">
          Press <kbd className="text-ink-300">esc</kbd> to close.
        </p>
      </div>
    </Modal>
  );
}
